import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  AlertTriangle, 
  Clock, 
  User, 
  PlayCircle, 
  PauseCircle, 
  Eye,
  Volume2
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { TriageDetailsModal } from "./TriageDetailsModal";

interface TriageSummaryCardProps {
  triageId: string;
  patientName: string;
  patientEmail?: string;
  symptoms: string[];
  urgency: string;
  status: string;
  createdAt: string;
  redFlags: string[];
  audioUrl?: string;
  className?: string;
}

export function TriageSummaryCard({
  triageId,
  patientName,
  patientEmail,
  symptoms,
  urgency,
  status,
  createdAt,
  redFlags,
  audioUrl,
  className
}: TriageSummaryCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency.toLowerCase()) {
      case 'immediate': return 'bg-red-500 text-white';
      case 'urgent': return 'bg-orange-500 text-white';
      case 'standard': return 'bg-yellow-500 text-black';
      case 'non-urgent': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'reviewed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePlayAudio = () => {
    if (!audioElement && audioUrl) {
      const audio = new Audio(audioUrl);
      setAudioElement(audio);
      audio.play();
      setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
    } else if (audioElement) {
      if (isPlaying) {
        audioElement.pause();
      } else {
        audioElement.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const openDetailsModal = () => {
    setIsDetailsModalOpen(true);
  };
  
  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
  };

  return (
    <>
      <Card className={cn("overflow-hidden border-l-4", {
        "border-l-red-500": urgency.toLowerCase() === "immediate",
        "border-l-orange-500": urgency.toLowerCase() === "urgent",
        "border-l-yellow-500": urgency.toLowerCase() === "standard",
        "border-l-green-500": urgency.toLowerCase() === "non-urgent",
        "border-l-gray-500": !["immediate", "urgent", "standard", "non-urgent"].includes(urgency.toLowerCase())
      }, className)}>
        <CardHeader className="pb-2 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className={getUrgencyColor(urgency)}>
                {urgency.toUpperCase()}
              </Badge>
              <Badge className={getStatusColor(status)}>
                {status}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              {format(new Date(createdAt), 'MMM dd, yyyy h:mm a')}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="font-medium">
                  {patientName}
                </span>
                {patientEmail && (
                  <span className="text-sm text-gray-500">
                    ({patientEmail})
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600">
                {symptoms.slice(0, 3).join(', ')}
                {symptoms.length > 3 && '...'}
              </div>
            </div>

            <div>
              {redFlags.length > 0 && (
                <div className="flex items-start gap-2 text-red-600 text-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <div>
                    {redFlags.slice(0, 2).join(', ')}
                    {redFlags.length > 2 && '...'}
                  </div>
                </div>
              )}
              
              {audioUrl && (
                <div className="mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 text-blue-600 border-blue-200"
                    onClick={handlePlayAudio}
                  >
                    {isPlaying ? (
                      <>
                        <PauseCircle className="w-4 h-4" />
                        Pause Audio
                      </>
                    ) : (
                      <>
                        <PlayCircle className="w-4 h-4" />
                        Play Patient Audio
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 flex justify-end gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={openDetailsModal}
              className="flex items-center gap-1"
            >
              <Eye className="h-4 w-4" />
              View Details
            </Button>
            {status.toLowerCase() === 'submitted' && (
              <Button size="sm">
                Review Now
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Triage Details Modal */}
      <TriageDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={closeDetailsModal}
        triageId={triageId}
      />
    </>
  );
}
